import React from 'react';
import { X, AlertTriangle, ExternalLink } from 'lucide-react';
import { CURRENT_VERSION } from '../../../config/pageBuilderVersion';

/**
 * Modal shown when sections with incompatible versions are detected.
 * Displays which sections couldn't be loaded and provides options for the user.
 */
export default function IncompatibleVersionModal({ isOpen, onClose, incompatibleSections = [] }) {
  if (!isOpen || incompatibleSections.length === 0) {
    return null;
  }

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Version Incompatibility</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-amber-100 rounded-md transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          <p className="text-sm text-gray-600 mb-4">
            The following {incompatibleSections.length === 1 ? 'section was' : 'sections were'} created
            with an older version of the page builder and {incompatibleSections.length === 1 ? 'is' : 'are'} not
            compatible with the current version ({CURRENT_VERSION}).
          </p>

          {/* List of incompatible sections */}
          <div className="space-y-2 mb-4">
            {incompatibleSections.map((section, idx) => (
              <div
                key={section.id || idx}
                className="p-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {section.content?.name || section.section_type || `Section ${idx + 1}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      Type: {section.section_type}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      v{section.version}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>What you can do:</strong>
            </p>
            <ul className="mt-2 text-sm text-blue-600 list-disc list-inside space-y-1">
              <li>Contact support for assistance with migrating these sections</li>
              <li>Recreate the sections using the current page builder</li>
              <li>Check if a newer version of the page is available</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <a
            href="mailto:support@example.com?subject=Page Builder Version Migration Request"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Contact Support
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  );
}
